/**
 * `/quote/sequences/:id` — read-only sequence detail view.
 *
 * Shows sequence metadata + each step's full templates. Editing a sequence
 * isn't supported in v1 — the backend has no PATCH endpoint and a
 * delete-then-recreate cycle would change the ID and break any active
 * enrollments. When PATCH lands, this page is the natural host for an
 * "Edit" CTA.
 */
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteQuoteSequence, useQuoteSequence } from "@/hooks/use-quote";
import { formatTimestamp } from "@/lib/formatters";

export function QuoteSequenceDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const seq = useQuoteSequence(id);
  const deleteMut = useDeleteQuoteSequence();

  function onDelete() {
    if (!seq.data) return;
    if (
      !window.confirm(
        `Delete sequence "${seq.data.name}"? Active enrollments will continue running their already-scheduled steps but no new ones will fire.`,
      )
    ) {
      return;
    }
    deleteMut.mutate(seq.data.id, {
      onSuccess: () => navigate("/quote/sequences"),
    });
  }

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1
          className="text-2xl font-extrabold tracking-tight text-gray-900"
          data-testid="quote-seq-detail-heading"
        >
          Sequence detail
        </h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          <button
            type="button"
            className="underline-offset-2 hover:underline"
            onClick={() => navigate("/quote/sequences")}
            data-testid="quote-seq-detail-back-link"
          >
            ← Back to sequences
          </button>
        </p>
      </header>

      {seq.isLoading && <Skeleton className="h-32 w-full" data-testid="quote-seq-detail-skeleton" />}

      {seq.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive" data-testid="quote-seq-detail-error">
              Couldn't load sequence: {seq.error.message}
            </p>
          </CardContent>
        </Card>
      )}

      {seq.data && (
        <>
          <Card data-testid="quote-seq-detail-meta-card">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle data-testid="quote-seq-detail-name">{seq.data.name}</CardTitle>
                  <CardDescription>
                    {seq.data.description ?? "—"} · created {formatTimestamp(seq.data.created_at)}
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDelete}
                  disabled={deleteMut.isPending}
                  data-testid="quote-seq-detail-delete-cta"
                >
                  {deleteMut.isPending ? "Deleting…" : "Delete"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex flex-wrap items-center gap-2 pt-0">
              <Badge
                variant={seq.data.is_active ? "default" : "outline"}
                data-testid="quote-seq-detail-active-badge"
              >
                {seq.data.is_active ? "active" : "inactive"}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {seq.data.steps.length} step{seq.data.steps.length === 1 ? "" : "s"}
              </span>
            </CardContent>
          </Card>

          {seq.data.steps.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="quote-seq-detail-no-steps">
                  This sequence has no steps. That shouldn't happen — the schema requires at
                  least one step on create. Re-create the sequence with at least one step.
                </p>
              </CardContent>
            </Card>
          ) : (
            <ul className="flex flex-col gap-3" data-testid="quote-seq-detail-steps-list">
              {seq.data.steps.map((step, idx) => (
                <li key={step.id}>
                  <Card data-testid={`quote-seq-detail-step-${idx + 1}`}>
                    <CardHeader>
                      <CardTitle className="text-base">Step {idx + 1}</CardTitle>
                      <CardDescription>
                        Drafts {step.delay_hours}h after{" "}
                        {idx === 0 ? "the lead is enrolled" : `step ${idx} sends`}.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Subject
                        </p>
                        <p
                          className="mt-1 text-sm text-gray-800"
                          data-testid={`quote-seq-detail-step-${idx + 1}-subject`}
                        >
                          {step.subject_template}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Body
                        </p>
                        <p
                          className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800"
                          data-testid={`quote-seq-detail-step-${idx + 1}-body`}
                        >
                          {step.body_template}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default QuoteSequenceDetailPage;
